import { useEffect, useRef, useContext } from 'react';
import socketService from '../services/socketService';
import { AuthContext } from '../context/AuthProvider';

/**
 * Hook for using WebSocket connection
 * Automatically connects when authenticated and disconnects on unmount
 */
export const useSocket = () => {
  const { token, isAuthenticated } = useContext(AuthContext);
  const isConnectedRef = useRef(false);

  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated && token && !isConnectedRef.current) {
      socketService.connect(token);
      isConnectedRef.current = true;
    }

    return () => {
      // Don't disconnect on unmount - keep connection alive across page navigation
      // Only disconnect on logout (handled separately)
    };
  }, [isAuthenticated, token]);

  return {
    socket: socketService,
    connected: socketService.connected,
  };
};

/**
 * Hook for subscribing to specific socket events
 * @param {string} event - Event name to subscribe to
 * @param {Function} callback - Callback to execute when event received
 * @param {Array} deps - Dependencies array for callback
 */
export const useSocketEvent = (event, callback, deps = []) => {
  const { token, isAuthenticated } = useContext(AuthContext);
  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...deps]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    // Connect if not already connected
    if (!socketService.connected) {
      socketService.connect(token);
    }

    // Create stable handler that uses ref
    const handler = (...args) => {
      callbackRef.current(...args);
    };

    // Subscribe to event
    socketService.on(event, handler);

    // Cleanup - unsubscribe from event
    return () => {
      socketService.off(event, handler);
    };
  }, [event, isAuthenticated, token]);
};

/**
 * Hook specifically for appointment changes
 * @param {Function} onAppointmentChange - Callback when appointment changes
 */
export const useAppointmentSocket = (onAppointmentChange) => {
  useSocketEvent('output:appointment:change', (data) => {
    console.log('[Socket] Appointment change received:', data);
    if (onAppointmentChange) {
      onAppointmentChange(data);
    }
  }, [onAppointmentChange]);
};

/**
 * Hook specifically for installation schedule changes
 * @param {Function} onInstallationChange - Callback when installation changes
 */
export const useInstallationSocket = (onInstallationChange) => {
  useSocketEvent('output:installation:change', (data) => {
    console.log('[Socket] Installation change received:', data);
    if (onInstallationChange) {
      onInstallationChange(data);
    }
  }, [onInstallationChange]);
};

export default useSocket;

